// Deployment trigger: 2026-04-24
import React, { useState, useEffect, useRef } from 'react';
import { Trophy, X, Sparkles, ArrowRight, Gift, Loader2 } from 'lucide-react';
import { Lead, Prize } from '../types';
import confetti from 'canvas-confetti';

interface LiveDrawProps {
  leads: Lead[];
  prizes: Prize[];
  onClose: () => void;
  onDrawComplete: (winners: Lead[], prize: Prize) => Promise<void>;
}

const LiveDraw: React.FC<LiveDrawProps> = ({ leads, prizes, onClose, onDrawComplete }) => {
  const [currentPrizeIndex, setCurrentPrizeIndex] = useState(0);
  const [localLeads, setLocalLeads] = useState<Lead[]>(leads);
  const [drawing, setDrawing] = useState(false);
  const [currentName, setCurrentName] = useState('???');
  const [winners, setWinners] = useState<Lead[]>([]);
  const [step, setStep] = useState<'idle' | 'drawing' | 'done'>('idle');
  const [isSaving, setIsSaving] = useState(false);
  
  const currentPrize = prizes[currentPrizeIndex];
  const eligibleLeads = localLeads.filter(l => !l.prizeWon || l.prizeWon === 'Raffle Entry' || l.prizeWon === 'N/A');
  const winnerCount = currentPrize?.winnersCount || 1;

  const triggerConfetti = () => {
    const duration = 3 * 1000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#f59e0b', '#10b981', '#6366f1']
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#f59e0b', '#10b981', '#6366f1']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };

  const startDraw = () => {
    if (eligibleLeads.length === 0) return;
    
    setStep('drawing');
    setDrawing(true);
    
    const actualCount = Math.min(winnerCount, eligibleLeads.length);
    const shuffled = [...eligibleLeads].sort(() => 0.5 - Math.random());
    const selectedWinners = shuffled.slice(0, actualCount);

    let iterations = 0;
    const maxIterations = 40;
    
    const drawNext = () => {
      const randomLead = eligibleLeads[Math.floor(Math.random() * eligibleLeads.length)];
      setCurrentName(randomLead.name || randomLead.email || 'Anonymous');
      iterations++;
      
      if (iterations >= maxIterations) {
        setWinners(selectedWinners);
        setCurrentName(selectedWinners[0]?.name || selectedWinners[0]?.email || 'Anonymous');
        setDrawing(false);
        setStep('done');
        triggerConfetti();
        
        setIsSaving(true);
        onDrawComplete(selectedWinners, currentPrize).then(() => {
          setIsSaving(false);
          const newLocalLeads = localLeads.map(l => {
            if (selectedWinners.find(w => w.id === l.id)) {
              return { ...l, prizeWon: currentPrize.name };
            }
            return l;
          });
          setLocalLeads(newLocalLeads);
        });
      } else {
        const delay = Math.max(50, iterations * 5);
        setTimeout(drawNext, delay);
      }
    };
    
    drawNext();
  };

  const handleNextPrize = () => {
    if (currentPrizeIndex < prizes.length - 1) {
      setCurrentPrizeIndex(currentPrizeIndex + 1);
      setStep('idle');
      setWinners([]);
      setCurrentName('???');
    } else {
      onClose();
    }
  };

  if (!currentPrize) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900 flex flex-col items-center justify-center overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-[100px]" />
      </div>

      <button 
        onClick={onClose}
        className="absolute top-8 right-8 p-4 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-10"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="relative z-10 w-full max-w-4xl px-6 text-center">
        {step === 'idle' && (
          <div className="animate-in zoom-in duration-500">
            {currentPrize.imageUrl ? (
              <img src={currentPrize.imageUrl} alt={currentPrize.name} className="w-48 h-48 object-cover rounded-3xl mx-auto mb-8 shadow-[0_0_40px_rgba(251,191,36,0.3)] border-4 border-white/10" referrerPolicy="no-referrer" />
            ) : (
              <Trophy className="w-32 h-32 text-amber-400 mx-auto mb-8 drop-shadow-[0_0_30px_rgba(251,191,36,0.5)]" />
            )}
            <h1 className="text-6xl md:text-8xl font-black text-white uppercase italic tracking-tighter mb-6">
              Ready to Draw?
            </h1>
            <div className="inline-flex items-center justify-center gap-3 px-6 py-3 bg-white/10 rounded-full mb-8 backdrop-blur-sm border border-white/10">
              <Gift className="w-6 h-6 text-amber-400" />
              <p className="text-2xl text-amber-400 font-black uppercase tracking-widest">
                {currentPrize.name}
              </p>
            </div>
            
            {eligibleLeads.length > 0 ? (
              <>
                <p className="text-xl text-indigo-200 mb-12 font-medium tracking-wide">
                  {eligibleLeads.length} eligible entries for {winnerCount} winner{winnerCount > 1 ? 's' : ''}
                </p>
                <button
                  onClick={startDraw}
                  className="px-12 py-6 bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-300 hover:to-amber-500 text-slate-900 text-2xl font-black uppercase tracking-widest rounded-full shadow-[0_0_40px_rgba(251,191,36,0.4)] hover:shadow-[0_0_60px_rgba(251,191,36,0.6)] hover:scale-105 transition-all duration-300 flex items-center gap-4 mx-auto"
                >
                  <Sparkles className="w-8 h-8" />
                  Start Draw
                </button>
              </>
            ) : (
              <div className="bg-red-500/20 border border-red-500/50 rounded-2xl p-8 max-w-lg mx-auto backdrop-blur-sm">
                <p className="text-2xl text-white font-bold mb-2">No Eligible Entries</p>
                <p className="text-red-200">All entries have already won a prize or there are no entries yet.</p>
                <button
                  onClick={handleNextPrize}
                  className="mt-6 px-8 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full font-bold uppercase tracking-widest transition-all"
                >
                  {currentPrizeIndex < prizes.length - 1 ? 'Skip to Next Prize' : 'Finish'}
                </button>
              </div>
            )}
            
            {prizes.length > 1 && (
              <div className="mt-12 flex items-center justify-center gap-2">
                {prizes.map((_, idx) => (
                  <div 
                    key={idx} 
                    className={`h-2 rounded-full transition-all duration-300 ${idx === currentPrizeIndex ? 'w-8 bg-amber-400' : idx < currentPrizeIndex ? 'w-2 bg-emerald-400' : 'w-2 bg-white/20'}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {step === 'drawing' && (
          <div className="animate-in fade-in duration-300">
            <h2 className="text-3xl text-indigo-300 font-bold uppercase tracking-widest mb-12 animate-pulse">
              Drawing {currentPrize.name}...
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-[3rem] p-12 backdrop-blur-sm shadow-2xl">
              <div className="text-5xl md:text-7xl font-black text-white truncate tracking-tight">
                {currentName}
              </div>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="animate-in zoom-in duration-700">
            {currentPrize.imageUrl ? (
              <img src={currentPrize.imageUrl} alt={currentPrize.name} className="w-32 h-32 object-cover rounded-3xl mx-auto mb-8 shadow-[0_0_40px_rgba(251,191,36,0.3)] border-4 border-white/10" referrerPolicy="no-referrer" />
            ) : (
              <Trophy className="w-24 h-24 text-amber-400 mx-auto mb-8 drop-shadow-[0_0_30px_rgba(251,191,36,0.5)]" />
            )}
            <h2 className="text-4xl text-amber-400 font-black uppercase tracking-widest mb-12">
              {currentPrize.name} {winners.length > 1 ? 'Winners!' : 'Winner!'}
            </h2>
            
            <div className="grid gap-6 max-w-2xl mx-auto max-h-[50vh] overflow-y-auto pr-4 custom-scrollbar mb-12">
              {winners.map((winner, idx) => (
                <div 
                  key={winner.id}
                  className="bg-white/10 border border-white/20 rounded-3xl p-8 backdrop-blur-md flex items-center gap-6 transform hover:scale-[1.02] transition-transform"
                  style={{ animationDelay: `${idx * 150}ms` }}
                >
                  <div className="w-16 h-16 rounded-full bg-amber-400/20 flex items-center justify-center text-amber-400 text-2xl font-black shrink-0">
                    #{idx + 1}
                  </div>
                  <div className="text-left overflow-hidden">
                    <div className="text-3xl font-black text-white truncate mb-2">
                      {winner.name || 'Anonymous'}
                    </div>
                    <div className="text-indigo-200 font-medium truncate">
                      {winner.email}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <button
              onClick={handleNextPrize}
              disabled={isSaving}
              className="px-12 py-5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full font-black text-xl uppercase tracking-widest transition-all shadow-lg shadow-indigo-500/20 hover:scale-105 flex items-center gap-3 mx-auto disabled:opacity-50 disabled:hover:scale-100"
            >
              {isSaving ? (
                <><Loader2 className="w-6 h-6 animate-spin" /> Saving...</>
              ) : currentPrizeIndex < prizes.length - 1 ? (
                <>Next Prize <ArrowRight className="w-6 h-6" /></>
              ) : (
                'Finish Draw'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveDraw;
